import React, { useEffect, useMemo, useRef, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { notification } from 'antd'
import { selfCheckinGuestRoomAutoAssign, selfCheckinRoomCleaningStatus } from '@/apis/aipass'
import { SearchReservationTypeWhenCheckedIn, SearchReservationTypeWhenNonCheckIn } from '@/models/reservation'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { checkActivePlugin, PluginCode } from '@/libs/plugins'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { chain, uniqBy, uniq, isArray } from 'lodash'
import { RoomCard } from '@/components/organisms/self-checkin/select-reservation/room-card'
import {
  CheckinCard,
  ReservationCard,
  ReservationWithNoRoomCard,
} from '@/components/organisms/self-checkin/select-reservation/reservation-card'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'

export const SelectReservation: React.FC<{}> = () => {
  const { state, saveState, stateErrorHandling } = useSelfCheckInState()
  const history = useHistory()
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()

  const [selectableReservations, setSelectableReservations] = useState<
    SearchReservationTypeWhenNonCheckIn[] | SearchReservationTypeWhenCheckedIn[]
  >([])

  const [selectedReservations, setSelectedReservations] = useState<
    SearchReservationTypeWhenNonCheckIn[] | SearchReservationTypeWhenCheckedIn[]
  >([])
  const [selectedGuestRoomAssignIds, setSelectedGuestRoomAssignIds] = useState<string[]>([])
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)
  const [singleAssignedReservation, setSingleAssignedReservation] = useState<SearchReservationTypeWhenNonCheckIn>()

  const isSubmittingRef = useRef(false)

  const submit = async () => {
    if (isSubmittingRef.current) {
      return
    }

    const selectedReservationAndRooms = selectedReservations.map(r => ({
      reservationId: r.reservationId,
      guestRoomAssignIds: r.reservationRoom
        .filter(r => selectedGuestRoomAssignIds.includes(r.guestRoomAssignId))
        .map(r => r.guestRoomAssignId),
    }))

    const _selectedReservations = selectedReservations.map(reservation => {
      const filteredRooms = reservation.reservationRoom.filter(room => selectedGuestRoomAssignIds.includes(room.guestRoomAssignId))
      const useSelectedRooms = filteredRooms.length > 0
      return {
        ...reservation,
        paxTotal: useSelectedRooms ? filteredRooms.reduce((carry, room) => carry + room.pax, 0) || reservation.paxTotal : 0,
        reservationRoom: filteredRooms,
      }
    })

    const selectedRoomPaxTotal = _selectedReservations.reduce((sum, r) => sum + r.paxTotal, 0)

    const preCheckedInPaxTotal =
      isCheckedInReservations(selectedReservations) && selectedReservations[0].accompany ? selectedReservations[0].accompany.length + 1 : 1

    const selectedReservationIds = selectedReservations.map(r => r.reservationId)

    isSubmittingRef.current = true

    try {
      if (
        checkActivePlugin(state.setting.plugin, PluginCode.CleaningManager) &&
        state.setting.accommodation.isCheckinAllowedBeforeRoomInspection === false
      ) {
        const { canCheckin } = await selfCheckinRoomCleaningStatus({
          hotelId: state.hotelId,
          guestRoomAssignIds: selectedGuestRoomAssignIds,
        })
        if (!canCheckin) {
          notification.warn({
            message: t('SelfCheckIn.room is not ready'),
            style: { whiteSpace: 'pre-line', width: 500, right: 100 },
          })
          return
        }
      }

      selfCheckinGuestRoomAutoAssign({ hotelId: state.hotelId, reservationIds: selectedReservationIds })

      saveState({
        ...state,
        checkinCustomize: undefined,
        confirm: undefined,
        accommodationInfo: undefined,
        uploadLicense: undefined,
        selectReservation: {
          hasSmartCheckin: _selectedReservations[0].hasSmartCheckin,
          hasAnyGuestCheckedIn: _selectedReservations[0].hasAnyGuestCheckedIn,
          hasIdentification: _selectedReservations[0].hasIdentification,
          selectedReservationIds,
          selectedReservations: _selectedReservations,
          selectedReservationAndRooms,
          paxTotal: selectedRoomPaxTotal || preCheckedInPaxTotal,
        },
      })

      history.push({
        pathname: _selectedReservations[0].hasAnyGuestCheckedIn
          ? '/self-checkin/additional-guest-info'
          : '/self-checkin/accommodation-info',
      })
    } catch (error) {
      errorHandler(error)
    } finally {
      isSubmittingRef.current = false
    }
  }

  const toggleRoom = (
    guestRoomAssignId: string,
    reservation: SearchReservationTypeWhenNonCheckIn | SearchReservationTypeWhenCheckedIn[],
  ) => {
    const targetReservations = isArray(reservation) ? reservation : [reservation]
    // 異なるチェックインの予約は同時に選択できないため、選択を切り替える
    if (
      isCheckedInReservations(targetReservations) &&
      selectedCheckinId !== undefined &&
      targetReservations[0].checkinId !== selectedCheckinId
    ) {
      setSelectedReservations(targetReservations)
      setSelectedGuestRoomAssignIds([guestRoomAssignId])
      return
    }
    const isSelected = selectedGuestRoomAssignIds.includes(guestRoomAssignId)
    if (isSelected) {
      const filteredIds = selectedGuestRoomAssignIds.filter(assignId => assignId !== guestRoomAssignId)
      setSelectedGuestRoomAssignIds([...filteredIds])
      setSelectedReservations(prev =>
        prev.filter(selectedReservation => {
          const targetReservation = targetReservations.find(r => r.reservationId === selectedReservation.reservationId)
          if (!targetReservation) {
            return true
          }
          return targetReservation.reservationRoom.some(room => filteredIds.includes(room.guestRoomAssignId))
        }),
      )
      return
    }

    setSelectedGuestRoomAssignIds(prev => [...prev, guestRoomAssignId])
    setSelectedReservations(prev => uniqBy([...prev, ...targetReservations], 'reservationId'))
  }

  const toggleCheckin = (reservations: SearchReservationTypeWhenCheckedIn[]) => {
    if (isCheckedInReservations(selectedReservations) && reservations[0].checkinId === selectedReservations[0].checkinId) {
      return
    }
    setSelectedReservations(reservations)
    const targetGuestName = reservations[0].guestName
    const sameGuestNameRoomIds = reservations.flatMap(reservation =>
      reservation.reservationRoom.filter(room => room.guestName === targetGuestName).map(room => room.guestRoomAssignId),
    )
    if (sameGuestNameRoomIds.length === 0) {
      sameGuestNameRoomIds.push(...reservations.flatMap(r => r.reservationRoom.map(room => room.guestRoomAssignId)))
    }
    setSelectedGuestRoomAssignIds(sameGuestNameRoomIds)
  }

  const toggleReservation = (reservation: SearchReservationTypeWhenNonCheckIn) => {
    const isSelected = selectedReservations.some(r => r.reservationId === reservation.reservationId)
    if (isSelected) {
      setSelectedReservations(prev => prev.filter(r => r.reservationId !== reservation.reservationId))
      const uncheckedIds = reservation.reservationRoom.map(room => room.guestRoomAssignId)
      setSelectedGuestRoomAssignIds(prev => [...prev.filter(assignId => !uncheckedIds.includes(assignId))])
      return
    }
    setSelectedReservations(prev => uniqBy([...prev, reservation], 'reservationId'))
    const targetGuestName = reservation.guestName
    const sameGuestNameRoomIds = reservation.reservationRoom
      .filter(room => room.guestName === targetGuestName)
      .map(room => room.guestRoomAssignId)
    if (sameGuestNameRoomIds.length === 0) {
      sameGuestNameRoomIds.push(...reservation.reservationRoom.map(room => room.guestRoomAssignId))
    }
    setSelectedGuestRoomAssignIds(prev => uniq([...prev, ...sameGuestNameRoomIds]))
  }

  const isCheckedInReservations = (
    reservations: SearchReservationTypeWhenCheckedIn[] | SearchReservationTypeWhenNonCheckIn[],
  ): reservations is SearchReservationTypeWhenCheckedIn[] => {
    return reservations.length > 0 && 'checkinId' in reservations[0]
  }

  const selectedCheckinId = useMemo(() => {
    if (!selectedReservations.length) {
      return undefined
    }
    return isCheckedInReservations(selectedReservations) ? selectedReservations[0].checkinId : undefined
  }, [selectedReservations])

  const isValidForm = useMemo(
    () => selectedReservations.length > 0 && selectedGuestRoomAssignIds.length > 0,
    [selectedReservations, selectedGuestRoomAssignIds],
  )

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'searchReservation')) {
      return
    }

    let _selectableReservations: SearchReservationTypeWhenNonCheckIn[] | SearchReservationTypeWhenCheckedIn[] = []
    if (state.searchReservation.result.preCheckinReservations?.length) {
      _selectableReservations = state.searchReservation.result.preCheckinReservations
    } else if (state.searchReservation.result.notCheckinReservations?.length) {
      _selectableReservations = state.searchReservation.result.notCheckinReservations
    }
    setSelectableReservations(_selectableReservations)
    const assignedReservations = _selectableReservations.filter(r => r.reservationRoom.length > 0)
    if (assignedReservations.length === 1) {
      setSingleAssignedReservation(assignedReservations[0])
    }

    if (state.selectReservation) {
      setSelectedGuestRoomAssignIds(
        state.selectReservation.selectedReservationAndRooms.flatMap(({ guestRoomAssignIds }) => guestRoomAssignIds),
      )
      setSelectedReservations(
        _selectableReservations.filter(r => state.selectReservation?.selectedReservationIds.includes(r.reservationId)),
      )
      setIsInitializeComponent(true)
      return
    }

    // チェックイン済みは1つ目のチェックインを予約選択と同じ挙動で初期選択する
    if (isCheckedInReservations(_selectableReservations)) {
      const defaultCheckinId = (_selectableReservations.find(r => r.reservationRoom.length > 0) ?? _selectableReservations[0]).checkinId
      toggleCheckin(_selectableReservations.filter(r => r.checkinId === defaultCheckinId))
      setIsInitializeComponent(true)
      return
    }

    const isOnlySelectableOneRoom = assignedReservations.length === 1 && assignedReservations[0].reservationRoom.length === 1
    if (isOnlySelectableOneRoom) {
      _selectableReservations.forEach(r => toggleReservation(r))
      setIsInitializeComponent(true)
      return
    }

    // 部屋が未アサインの予約しかない場合は、初期選択の基準になるゲスト名を部屋から取れない
    if (assignedReservations.length === 0) {
      setIsInitializeComponent(true)
      return
    }

    const defaultGuestName = assignedReservations[0].guestName
    const sameGuestRoomReservations = assignedReservations.filter(
      r => r.reservationRoom.filter(room => room.guestName === defaultGuestName).length >= 1,
    )
    if (sameGuestRoomReservations.length === 0) {
      setIsInitializeComponent(true)
      return
    }
    const noneAssignedReservations = _selectableReservations.filter(r => r.reservationRoom.length === 0)
    sameGuestRoomReservations.concat(noneAssignedReservations).forEach(r => toggleReservation(r))
    setIsInitializeComponent(true)
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }

  return (
    <SelfCheckinLayout
      state={state}
      header={{ goToPreviousPage: history.goBack, title: t('Select reservation') }}
      subtitle={{ title: t('SelfCheckIn.SelectReservations') }}
      footer={{ right: { isDisabled: !isValidForm, label: t('Next'), onClick: submit } }}
    >
      <div css={selectableReservationStyle} className="selectable-reservation-container">
        <div className="container-row title-block">
          <div className="container-title">{t('Reservation Selection')}</div>
          <div className="container-title">{t('Rooms Selection')}</div>
        </div>

        {isCheckedInReservations(selectableReservations) &&
          chain(selectableReservations)
            .groupBy(r => r.checkinId)
            .map((reservations, checkinId) => (
              <div key={checkinId} className="container-row">
                <div>
                  <CheckinCard
                    reservations={reservations}
                    value={selectedCheckinId}
                    displayType={state.setting.accommodation.roomOptionDisplayType}
                    onClick={() => toggleCheckin(reservations)}
                  />
                </div>
                <div>
                  {reservations.flatMap(reservation =>
                    reservation.reservationRoom.map((room, i) => (
                      <RoomCard
                        key={room.guestRoomAssignId}
                        value={selectedGuestRoomAssignIds}
                        room={room}
                        displayType={state.setting.accommodation.roomOptionDisplayType}
                        onClick={() => toggleRoom(room.guestRoomAssignId, reservations)}
                      />
                    )),
                  )}
                </div>
              </div>
            ))
            .value()}

        {!isCheckedInReservations(selectableReservations) &&
          (singleAssignedReservation ? (
            <div className="container-row">
              <ReservationWithNoRoomCard
                reservation={singleAssignedReservation}
                reservationGroups={selectableReservations}
                value={selectedReservations as SearchReservationTypeWhenNonCheckIn[]}
                onClick={() => selectableReservations.forEach(r => toggleReservation(r))}
                displayType={state.setting.accommodation.roomOptionDisplayType}
              />
              <div>
                {singleAssignedReservation.reservationRoom.map((room, i) => (
                  <RoomCard
                    key={room.guestRoomAssignId}
                    value={selectedGuestRoomAssignIds}
                    room={room}
                    onClick={() => toggleRoom(room.guestRoomAssignId, singleAssignedReservation)}
                    displayType={state.setting.accommodation.roomOptionDisplayType}
                  />
                ))}
              </div>
            </div>
          ) : (
            selectableReservations.map(reservation => (
              <div key={reservation.reservationId} className="container-row">
                <ReservationCard
                  reservation={reservation}
                  value={selectedReservations as SearchReservationTypeWhenNonCheckIn[]}
                  onClick={() => toggleReservation(reservation)}
                  displayType={state.setting.accommodation.roomOptionDisplayType}
                />
                <div>
                  {reservation.reservationRoom.map((room, i) => (
                    <RoomCard
                      key={room.guestRoomAssignId}
                      value={selectedGuestRoomAssignIds}
                      room={room}
                      onClick={() => toggleRoom(room.guestRoomAssignId, reservation)}
                      displayType={state.setting.accommodation.roomOptionDisplayType}
                    />
                  ))}
                </div>
              </div>
            ))
          ))}
      </div>
    </SelfCheckinLayout>
  )
}

const selectableReservationStyle = css(
  {
    lineHeight: 1.5,
    '.container-row': {
      display: 'flex',
      gap: 32,
      '> div': { width: 'calc(50% - 16px)' },
    },
    '.container-title': {
      fontSize: 20,
      fontWeight: 500,
      letterSpacing: '1px',
      lineHeight: '27px',
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: {
      padding: '0 32px',
      '.container-row': {
        flexDirection: 'row',
        gap: 16,
        '> div': { width: 'calc(50% - 8px)' },
      },
    },
    landscape13: {
      '.container-row': { gap: 32, '> div': { width: 'calc(50% - 16px)' } },
      '.container-title': { fontSize: 24, letterSpacing: '1.2px', lineHeight: '30px' },
    },
  }),
)
